import { Page } from "@boundary/layouts/pageLayout";
import { Input } from "@boundary/components/input";
import { OnboardingElement } from "@boundary/widgets/onboarding";
import { IMaskInput } from "react-imask";
import { useForm, ControllerRenderProps } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form, FormField, FormMessage } from "@boundary/components/form";
import { LoginFlowData, LoginFlowSteps } from "@boundary/pages/3.login/loginFlow";
import { Button } from "@boundary/components/button";
import { useAuth } from "@boundary/_hooks";
import { useRef, useEffect } from "react";
import styles from "./phone.module.scss";

export const PhoneStep: OnboardingElement<LoginFlowData, LoginFlowSteps> = (props) => {
  const { requestOtp } = useAuth();
  const inputRef = useRef<HTMLInputElement>(null);
  const maskRef = useRef<any>(null);
  const form = useForm<LoginFlowData>({
    resolver: zodResolver(
      LoginFlowData.pick({
        phone: true
      })
    )
  });

  const handleSubmit = form.handleSubmit(async (data: Pick<LoginFlowData, "phone">) => {
    await requestOtp(data.phone);
    return props.onComplete("otp", data);
  });

  // Устанавливаем курсор после "+7 (" (позиция 4) при клике/фокусе
  const handleClick = () => {
    // Используем requestAnimationFrame для более надежной установки курсора
    const setCursor = () => {
      if (inputRef.current) {
        try {
          // Позиция 4 = после "+7 ("
          inputRef.current.setSelectionRange(4, 4);
        } catch (e) {
          // Игнорируем ошибки
        }
      }
      // Также пробуем через API IMask
      if (maskRef.current && maskRef.current.masked) {
        try {
          maskRef.current.masked.cursor = 4;
          maskRef.current.masked.updateCursor();
        } catch (e) {
          // Игнорируем ошибки
        }
      }
    };
    
    // Пробуем установить курсор несколько раз с использованием requestAnimationFrame
    requestAnimationFrame(setCursor);
    requestAnimationFrame(() => {
      requestAnimationFrame(setCursor);
    });
    setTimeout(setCursor, 0);
    setTimeout(setCursor, 10);
    setTimeout(setCursor, 50);
    setTimeout(setCursor, 100);
  };

  // Устанавливаем курсор после монтирования компонента
  useEffect(() => {
    const setInitialCursor = () => {
      if (inputRef.current) {
        try {
          inputRef.current.setSelectionRange(4, 4);
        } catch (e) {
          // Игнорируем ошибки
        }
      }
      if (maskRef.current && maskRef.current.masked) {
        try {
          maskRef.current.masked.cursor = 4;
          maskRef.current.masked.updateCursor();
        } catch (e) {
          // Игнорируем ошибки
        }
      }
    };
    
    // Пробуем установить курсор после монтирования
    setTimeout(setInitialCursor, 0);
    setTimeout(setInitialCursor, 50);
    setTimeout(setInitialCursor, 150);
  }, []);

  return (
    <Page screenBackground="domain">
      <Page.SafeArea />
      <Page.DisplayZone>
        <Form {...form}>
          <form onSubmit={handleSubmit} className={styles.form}>
            <Page.DisplayZone.Body className={styles.body}>
              <h1>Привет! Введите свой<br />номер телефона 😜</h1>
              <p>Мы отправим вам сообщение с кодом для регистрации</p>
              <FormField
                control={form.control}
                name="phone"
                render={({ field }: { field: ControllerRenderProps<LoginFlowData, "phone"> }) => (
                  <IMaskInput
                    mask="+7 (000) 000-00-00"
                    value={field.value || ""}
                    lazy={false}
                    placeholderChar="*"
                    onAccept={(value) => {
                      field.onChange(value);
                    }}
                    unmask={false}
                    inputRef={(el: HTMLInputElement | null) => {
                      field.ref(el);
                      inputRef.current = el;
                    }}
                    maskRef={(mask: any) => {
                      maskRef.current = mask;
                      // Устанавливаем курсор после инициализации маски
                      if (mask && mask.masked) {
                        requestAnimationFrame(() => {
                          try {
                            mask.masked.cursor = 4;
                            mask.masked.updateCursor();
                          } catch (e) {
                            // Игнорируем ошибки
                          }
                        });
                      }
                    }}
                    onClick={handleClick}
                    onFocus={handleClick}
                    onBlur={field.onBlur}
                    type="tel"
                    placeholder="+7 (***) ***-**-**"
                    className={styles.input}
                  />
                )}
              />
              <FormField
                render={() => <FormMessage className={styles.formMessage} />}
                control={form.control}
                name="phone"
              />
            </Page.DisplayZone.Body>
            <Page.DisplayZone.Sticky className={styles.buttonPad}>
              <Button disabled={form.formState.isSubmitting}>Получить код</Button>
            </Page.DisplayZone.Sticky>
          </form>
        </Form>
      </Page.DisplayZone>
    </Page>
  );
};
